import { createContext, useContext, type ReactNode } from "react";

/**
 * Draws a label differently, e.g. with a reviewer's tracked change. Gets the label's raw text and the
 * input it belongs to (null outside an input, e.g. page text); returns null to draw it as usual.
 */
export type DecorateLabelT = (text: string, inputId: string | null) => ReactNode | null;

/** On the element wrapping a decorated label: its raw text, and its input id ("" outside an input). */
export const LABEL_TEXT_ATTRIBUTE = "data-label-text";
export const LABEL_INPUT_ATTRIBUTE = "data-label-input";

const DecorateLabelContext = createContext<DecorateLabelT | null>(null);
const LabelInputContext = createContext<string | null>(null);

/** Turns on label decoration below it. Only review screens use this; patients never see it. */
export function LabelDecorationProvider({ decorate, children }: { decorate: DecorateLabelT; children: ReactNode }) {
    return <DecorateLabelContext.Provider value={decorate}>{children}</DecorateLabelContext.Provider>;
}

/** Tells the labels below which input they belong to. */
export function LabelInputScope({ inputId, children }: { inputId: string; children: ReactNode }) {
    return <LabelInputContext.Provider value={inputId}>{children}</LabelInputContext.Provider>;
}

export function useLabelDecoration() {
    return { decorate: useContext(DecorateLabelContext), inputId: useContext(LabelInputContext) };
}

/**
 * A label's text, decorated when a review screen asks for it. RichLabel does this for markdown labels;
 * use this for labels shown as plain text.
 */
export function LabelText({ children, fallback }: { children: string; fallback?: ReactNode }) {
    const { decorate, inputId } = useLabelDecoration();
    if (!decorate) return <>{fallback ?? children}</>;
    return (
        <span {...{ [LABEL_TEXT_ATTRIBUTE]: children, [LABEL_INPUT_ATTRIBUTE]: inputId ?? "" }}>
            {decorate(children, inputId) ?? fallback ?? children}
        </span>
    );
}
